import { Confirm } from '@/components/main/Confirm'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuShortcut,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuthContext } from '@/context'
import { useImageCreditsEnabled } from '@/hooks/useImageCreditsEnabled'
import { useLogout } from '@/services/auth/mutations'
import { useQueryBillingUsage } from '@/services/billing/queries'
import { LogOut, SettingsIcon, Zap, Image as ImageIcon, PlusCircle } from 'lucide-react'
import { useNavigate } from 'react-router'

export function UserNav() {
    const { user, orgAccount, activeBusiness } = useAuthContext()
    const imageCreditsEnabled = useImageCreditsEnabled()
    const navigate = useNavigate()
    const { mutateAsync: logoutUser, isLoading: loggingOut } = useLogout({})

    const { data: usage } = useQueryBillingUsage({ 
        pathParams: { businessId: activeBusiness as string },
        enabled: !!activeBusiness 
    })

    const openProfilePage = () => {
        navigate('/dashboard/settings/personal')
    }

    const openBillingPage = () => {
        navigate('/dashboard/settings/usage')
    }

    const handleLogout = () => {
        localStorage.clear()
        logoutUser({})
        window.location.reload()
    }

    const aiRemaining = (usage?.ai_credits?.total ?? 0) - (usage?.ai_credits?.used ?? 0)
    const imgRemaining = (usage?.image_credits?.total ?? 0) - (usage?.image_credits?.used ?? 0)

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <div className='flex items-center gap-3 cursor-pointer group'>
                    <div className='hidden md:flex flex-col items-end'>
                        <span className='text-xs font-bold text-gray-500 uppercase tracking-wider'>Credits</span>
                        <div className='flex items-center gap-2'>
                            <div className='flex items-center gap-1 text-[11px] font-bold text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded'>
                                <Zap size={10} /> {aiRemaining.toLocaleString()}
                            </div>
                            {imageCreditsEnabled ? (
                                <div className='flex items-center gap-1 text-[11px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded'>
                                    <ImageIcon size={10} /> {imgRemaining.toLocaleString()}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <Button variant='ghost' className='relative w-8 h-8 rounded-full border-2 border-transparent group-hover:border-purple-200 transition-all'>
                        <Avatar className='w-8 h-8 rounded-full'>
                            <AvatarImage src={user?.profile_picture} alt='@shadcn' />
                            <AvatarFallback>
                                {user?.fullname
                                    ? user?.fullname
                                          .split(' ')
                                          .map(ch => ch![0])
                                          .join(' ')
                                    : null}
                            </AvatarFallback>
                        </Avatar>
                    </Button>
                </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent className='w-64' align='end' forceMount>
                <DropdownMenuLabel className='font-normal'>
                    <div className='flex flex-col space-y-1'>
                        <p className='text-sm font-medium leading-none'>{orgAccount?.org?.name}</p>
                        <p className='text-xs leading-none text-muted-foreground'>{user?.email}</p>
                    </div>
                </DropdownMenuLabel>
                
                <DropdownMenuSeparator />
                
                <div className='p-2 space-y-2'>
                    <div className='flex items-center justify-between px-2 py-1.5'>
                        <div className='flex items-center gap-2'>
                            <Zap size={14} className='text-purple-600' />
                            <span className='text-xs font-medium text-gray-600'>AI Credits</span>
                        </div>
                        <span className='text-xs font-bold'>{aiRemaining.toLocaleString()}</span>
                    </div>
                    {imageCreditsEnabled ? (
                        <div className='flex items-center justify-between px-2 py-1.5'>
                            <div className='flex items-center gap-2'>
                                <ImageIcon size={14} className='text-blue-600' />
                                <span className='text-xs font-medium text-gray-600'>Image Credits</span>
                            </div>
                            <span className='text-xs font-bold'>{imgRemaining.toLocaleString()}</span>
                        </div>
                    ) : null}
                    <Button 
                        variant='outline' 
                        size='sm' 
                        className='w-full h-8 text-[11px] font-bold border-dashed border-purple-200 text-purple-600 hover:bg-purple-50'
                        onClick={openBillingPage}
                    >
                        <PlusCircle size={12} className='mr-1' /> ADD CREDITS
                    </Button>
                </div>

                <DropdownMenuSeparator />
                
                <DropdownMenuGroup>
                    <DropdownMenuItem onSelect={openProfilePage}>
                        <SettingsIcon className='w-4 h-4 mr-2' />
                        <span>Profile</span>
                        <DropdownMenuShortcut>⇧⌘P</DropdownMenuShortcut>
                    </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                    <Confirm
                        trigger={
                            <DropdownMenuItem onSelect={e => e.preventDefault()}>
                                <LogOut className='w-4 h-4 mr-2' />
                                <span>Log out</span>
                            </DropdownMenuItem>
                        }
                        title='Log out?'
                        message='Are you sure you want to log out?'
                        primaryAction={handleLogout}
                        loading={loggingOut}
                    />
                </DropdownMenuGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}
